import interpolateComponents from '@automattic/interpolate-components';
import { useEffect, useState } from 'react';

import {
  SecondaryButtonRouterLink,
  TrackedSecondaryButtonLink,
} from 'components/Button';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { ShareURLButton } from 'components/ShareURLButton';
import { H2 } from 'components/Text/H2';
import { RichTextHeadline } from 'components/Text/RichTextHeadline';
import { Underline } from 'components/Text/Underline';
import { CmsApiRecentSalariesBlockDataWithAnalytics } from 'modules/cms/api/types/blocks/CmsApiRecentSalariesBlock';
import { salariesFetchRecentSubmissions } from 'modules/salaries/api/salariesFetchRecentSubmissions';
import { SalariesRecentSubmission } from 'modules/salaries/types/SalariesRecentSubmission';

import {
  RecentSubmissionsContainer,
  RecentSubmissionsHeading,
  RecentSubmissionsWrapper,
  StyledTable,
  SurveyButtonContainer,
  TableContainer,
  TableHeading,
  aCycle,
} from './RecentSubmissions.styled';

function formatSalary(salary: number): string {
  return salary.toLocaleString(CURRENT_LOCALE, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  });
}

function useSalaryRecentSubmissions() {
  const [recentSubmissions, setRecentSubmissions] =
    useState<SalariesRecentSubmission[]>();

  useEffect(() => {
    if (!recentSubmissions) {
      salariesFetchRecentSubmissions().then(
        (fetchedRecentSubmissions: SalariesRecentSubmission[]) =>
          setRecentSubmissions(fetchedRecentSubmissions),
      );
    }
  }, [recentSubmissions]);

  return recentSubmissions;
}

type RecentSubmissionsProps = {
  alreadySubmitted: boolean;
  surveyLoading: boolean;
  cmsData?: CmsApiRecentSalariesBlockDataWithAnalytics;
};

export function RecentSubmissions({
  alreadySubmitted,
  surveyLoading,
  cmsData,
}: RecentSubmissionsProps) {
  const recentSubmissions = useSalaryRecentSubmissions();

  const tableHeading = interpolateComponents({
    mixedString: getText('{{s}}Recent Salary Submissions{{/s}}'),
    components: {
      s: <Underline />,
    },
  });

  const shareUrl = {
    en: '/salaries/survey',
    es: '/salaries/survey',
    pt: '/salaries/survey',
  };

  const messageOverride = getText(
    'I just took the Idealist Salary Survey! Find out what you could be earning at a nonprofit in just a few quick steps.',
  );
  const emailMessageOverride = getText(
    'I just took the Idealist Salary Survey! Find out what you could be earning at a nonprofit in just a few quick steps. Start here:',
  );
  const emailSubjectOverride = getText('Check this out: Idealist Salaries');

  const data = recentSubmissions
    ? recentSubmissions
        .concat(recentSubmissions.slice(0, 4))
        .map((submission) => [
          submission.title,
          formatSalary(submission.salary),
          submission.location,
        ])
    : [];

  return (
    <RecentSubmissionsContainer>
      <RecentSubmissionsWrapper>
        <RecentSubmissionsHeading>
          {cmsData ? (
            <RichTextHeadline>{cmsData.headline}</RichTextHeadline>
          ) : (
            <H2>
              {getText('Help us research pay equity in the nonprofit sector')}
            </H2>
          )}

          {cmsData && (
            <TrackedSecondaryButtonLink
              href={cmsData.buttonUrl}
              $reverse
              eventName="CMS Block"
              eventProperties={{
                block_position: cmsData.blockIndex,
                block_type: 'Recent Salaries',
                cta_type: 'Button',
                button_text: cmsData.buttonText,
                page_updated: cmsData.pageUpdated,
              }}
            >
              {cmsData.buttonText}
            </TrackedSecondaryButtonLink>
          )}

          {!cmsData && !surveyLoading && (
            <SurveyButtonContainer>
              {alreadySubmitted ? (
                <ShareURLButton
                  buttonText="Share Our Survey"
                  shareUrl={shareUrl}
                  variant="secondary-white"
                  messageOverride={messageOverride}
                  emailMessageOverride={emailMessageOverride}
                  emailSubjectOverride={emailSubjectOverride}
                />
              ) : (
                <SecondaryButtonRouterLink to="/salaries/survey" reverse>
                  {getText('Take Our Survey')}
                </SecondaryButtonRouterLink>
              )}
            </SurveyButtonContainer>
          )}
        </RecentSubmissionsHeading>
        <TableContainer>
          <TableHeading>{tableHeading}</TableHeading>
          {!recentSubmissions ? (
            <LoadingIndicator />
          ) : (
            <StyledTable
              simple
              headings={[
                getText('Position'),
                getText('Salary'),
                getText('Location'),
              ]}
              data={data}
              $animationName={aCycle(data)}
            />
          )}
        </TableContainer>
      </RecentSubmissionsWrapper>
    </RecentSubmissionsContainer>
  );
}
